/**
 * Revenue Tracking Hook
 * 
 * Usage:
 * import { useRevenueTracking } from './hooks/useRevenueTracking'
 * 
 * const { trackEvent } = useRevenueTracking()
 * trackEvent('limit_reached', { tier: 'anonymous', searches: 3 })
 */

import { useAuth } from '../contexts/AuthContext'

export function useRevenueTracking() {
  const { user } = useAuth()

  const trackEvent = async (eventType: string, metadata: Record<string, any> = {}) => {
    try {
      const userId = user?.userId || `anonymous_${getAnonymousId()}`
      const apiUrl = import.meta.env.VITE_API_URL.replace(/\/$/, ''); // Remove trailing slash
      
      await fetch(`${apiUrl}/revenue-track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventType,
          userId,
          metadata: {
            ...metadata,
            userEmail: user?.email,
            userTier: metadata.tier || 'unknown',
            url: window.location.href,
            referrer: document.referrer,
          }
        })
      })
    } catch (error) {
      // Silent fail - don't break user experience
      console.error('Tracking error:', error)
    }
  }

  return { trackEvent }
}

// Get or create anonymous user ID
function getAnonymousId(): string {
  let id = localStorage.getItem('anonymous_id')
  if (!id) {
    id = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('anonymous_id', id)
  }
  return id
}

// Convenience functions for common events
export function useConversionTracking() {
  const { trackEvent } = useRevenueTracking()

  return {
    trackLimitReached: (tier: string, searches: number) => 
      trackEvent('limit_reached', { tier, searches }),
    
    trackUpgradePromptShown: (location: string) => 
      trackEvent('upgrade_prompt_shown', { location }),
    
    trackUpgradePromptClicked: (location: string) => 
      trackEvent('upgrade_prompt_clicked', { location }),
    
    trackCheckoutStarted: (solution: string, tier: string, amount: number) => 
      trackEvent('checkout_started', { solution, tier, amount }),
    
    trackCheckoutAbandoned: (step: string, reason?: string) => 
      trackEvent('checkout_abandoned', { step, reason }),
    
    trackPaymentCompleted: (solution: string, amount: number, paymentMethod: string) => 
      trackEvent('payment_completed', { solution, amount, paymentMethod }),
    
    trackPaymentFailed: (reason: string, amount: number, paymentMethod: string) => 
      trackEvent('payment_failed', { reason, amount, paymentMethod }),
    
    trackSearchPerformed: (query: string, resultsCount: number) => 
      trackEvent('search_performed', { query, resultsCount }),
    
    trackSolutionViewed: (solutionId: string, solutionName: string) => 
      trackEvent('solution_viewed', { solutionId, solutionName }),
  }
}
